/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BuilderNavigationService, BuilderUserContext, FilterNames, FilterRestService, HlpUpdateListingStatusDialogConfig, hlpUpdateListingStatusDialogRoute, } from '@gh/builder-shared';
import {
  AppRouter, DateTimeTypes, filterableForm, FilterableFormState, FormStateBuilder, getRouteInitData, NumberTypes, } from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource, SortDirection } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent, DialogService, noAnswer, NotificationService, WaitingService, yesAnswer, yesNoConfirmation, } from '@gh/core-ui';
import { setLocalValue } from '@gh/core-util';
import {
  buildDateTimeColumn, buildNumberColumnLeft, cursorTracker, GridDefinitionFactory, LayoutBasedGridDefinition, newGridPaginationCursor, } from '@gh/core-ui-grid';
import { TranslationService } from '@gh/core-mls';
import { HlpListBusinessService } from '../../resources/hlp-list-business.service';
import { HlpListItem, SalespersonSelectItem } from '../../resources/hlp.model';
import {
  buildHlpBrochureLink,
  BuilderPermission,
  HlpRestService,
  HlpStatus,
  ListingStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { map$, catchError$, filter$, switchAll$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { concat$, throwError$ } from '@gh/rx';
import { MessageBasket } from '@gh/core-messages';
import { isSalespersonSelectItemInactive } from '../../../leads';
import {
  HlpCreateLeadQuoteDialogConfig,
  hlpCreateLeadQuoteDialogRoute,
} from '../hlp-create-lead-quote-dialog/hlp-create-lead-quote-dialog.component';
import { HlpDetailsBusinessService } from '../../resources/hlp-details-business.service';
import { omit } from 'lodash';

@Component({
  standalone: false,
  selector:    'gh-hlp-list',
  styleUrls:   ['hlp-list.component.scss'],
  templateUrl: 'hlp-list.component.html',
})
export class HlpListComponent implements OnInit {
  dataSource: SlicedDataSource<any>;
  form: FilterableFormState<any>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor({ page: 0, pageSize: -1 , sort: { field: 'updatedDate', direction: SortDirection.Descending }});
  filter: any;
  hasCreatePermission: boolean;

  get hasEditPermission(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT);
  }

  get hasEditViewPermission(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
      || this.userContext.hasPermission(BuilderPermission.Sales.Hlp.VIEW);
  }

  constructor(
    private route: ActivatedRoute,
    private routeData: RouteDataService,
    private userContext: BuilderUserContext,
    private formStateBuilder: FormStateBuilder,
    private businessService: HlpListBusinessService,
    private hlpRest: HlpDetailsBusinessService,
    private notifications: NotificationService,
    private dialogService: DialogService,
    private router: AppRouter,
    private angRouter: Router,
    private translations: TranslationService,
    private dialogs: DialogService,
    private filterService: FilterRestService,
    private waitingService: WaitingService,
    private navigationService: BuilderNavigationService,
    public gridDefinitionFactory: GridDefinitionFactory,
    private hlpRestService: HlpRestService,
  ) {
    this.routeData.mergeQueryParams({ sortDirection: 'desc', sortField: 'updatedDate'});
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        updatedDate:  {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          sort: 'desc',
          comparator: () => 0,
        },
        addedDate:    buildDateTimeColumn(DateTimeTypes.DateTime),
        displayPrice: buildNumberColumnLeft(NumberTypes.Price),
        floorArea:    buildNumberColumnLeft(NumberTypes.PriceWithoutDecimals),
        status:       {
          minWidth: 95,
          cellClass: ({ data }: { data: any }) => (
            {
              [HlpStatus.Deleted]: 'cell--negative',
              [HlpStatus.Active]:  'cell--positive',
              [HlpStatus.Draft]:   'cell--unimportant',
            }[data.statusValue]
          ),
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id)
      },
    );
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout, createLeadQuote, clonedHlp, salesPerson } = getRouteInitData(this.route);
    let isInit = true;
    this.hasCreatePermission = this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE);
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        ...initFilter,
        salesPerson,
      },
      children: {
        searchString:  void 0,
        searchInSiteAddress: void 0,
        salesPersonId: void 0,
        salesPerson: void 0,
        showPublished: void 0,
        showDraft: void 0,
        showDeleted: void 0,
      },
    }));
    const tracker = this.routeData.tracker(filterTracker(this.form)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = {
        ...omit(filter, 'salesPersonId', 'salesPerson'),
        searchInSiteAddress: true,
      };
      if (filter.salesPerson && filter.salesPerson.id) {
        this.filter.salesPersonId = filter.salesPerson.id;
      }
      if (!isInit) {
        this.filterService.update(FilterNames.hlpList, this.filter).subscribe();
      }
      isInit = false;
      this.dataSource = this.businessService.findAllAsDataSource(this.filter);
    });
    if (createLeadQuote) {
      this.onCreateQuote({ id: createLeadQuote } as HlpListItem);
      this.router.switchTo([], {
        omitQueryParams: ['createLeadQuote'],
      });
    }
    if (clonedHlp) {
      this.hlpRest.get(clonedHlp).subscribe((hlpDetails) => {
        this.onClone({
          id: hlpDetails.houseAndLandPackageId, hlpNumber: hlpDetails.hlpNumber, quoteDescr: hlpDetails.quoteDescription,
        } as HlpListItem);
      });

      this.router.switchTo([], {
        omitQueryParams: ['clonedHlp'],
      });
    }
  }

  isLinkVisible(hlp: HlpListItem): boolean {
    return hlp.status === HlpStatus.Active && !!hlp.websiteLink;
  }

  getBrochureLink(hlp: HlpListItem): string {
    return buildHlpBrochureLink(hlp.websiteLink);
  }

  goToAel(hlp?: HlpListItem): void {
    if (hlp) {
      const navigationParams = {
        parentTitle: 'lbl_hl',
        queryParams: {
          label:      'lbl_hlps',
          entityType: 'HLP',
          entityId:   hlp.id,
        },
      };
      this.router.navigateToChild([`ael`], navigationParams);
      return;
    }

    this.navigationService.goToAel({
      label:      'lbl_hlps',
      entityType: 'HLP',
      eventTypeIds: [266, 267, 268, 269, 270, 271, 272],
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onReset(): void {
    this.form.emitFilter();
  }

  goToHlpDetails(hlp: HlpListItem): void {
    if (!this.hasEditViewPermission) {
      return;
    }
    this.router.navigateToChild(['/sales/hlp', hlp.id], {
      parentTitle: 'lbl_hlps',
    });
  }

  loadSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findSalesPersonsByName(event.query, {
      showInactive: true,
    }));
  }

  onSalesPersonSelected(salesPerson: SalespersonSelectItem): void {
    if (!salesPerson || salesPerson?.id !== this.form.getControl('salesPersonId').value) {
      this.form.getControl('salesPersonId').setValue(salesPerson?.id || '');
      this.form.emitFilter();
    }
  }

  onCreate(): void {
    this.router.navigateToChild(['/sales/hlp/new'], {
      parentTitle: 'lbl_hlps',
    });
  }

  isDeleteShow(hlp: HlpListItem): boolean {
    return (
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.DELETE)
        || this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      )
      && hlp.statusValue !== HlpStatus.Deleted;
  }

  onDeleteClick(hlp: HlpListItem): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_hlp_deletion')).pipe(
      filter$(yesAnswer)).subscribe(() => {
        if (hlp.statusValue === HlpStatus.Active) {
          this.hlpRest.getHlpInfo(hlp.id).subscribe((hlpDetails) => {
            if (hlpDetails.publishedOnDomainComAu || hlpDetails.publishedOnRealestateComAu) {
              this.dialogService.openWithInput(hlpUpdateListingStatusDialogRoute, <HlpUpdateListingStatusDialogConfig>{
                  hlp: hlpDetails,
                },
                                               {
                  autoFocus: false,
                },
              ).afterClosed().pipe(
                filter$(Boolean))
                .subscribe(({ continueDeleting, listingStatus }) => {
                  if (continueDeleting) {
                    this.deleteHlp(hlp, listingStatus);
                  }
                });
              return;
            }
            this.deleteHlp(hlp);
          });
          return;
        }
        this.deleteHlp(hlp);
    });
  }

  canBeEditable(hlp: HlpListItem): boolean {
    return hlp.status !== HlpStatus.Deleted;
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onClone(hlp: HlpListItem): void {
      if (hlp.id) {
        const hlpId = hlp.id;
        const cloned$ = this.cloneHlp(hlpId, false);
        cloned$.pipe(
          this.waitingService.operator(),
          catchError$((errors) => {
            if (errors.has('msg_pb_withdrawn_warning_hlp')) {
              const answer$ = this.dialogService.confirmation(yesNoConfirmation('msg_pb_withdrawn_warning_hlp'));
              const yesAnswer$ = answer$.pipe(filter$(yesAnswer), switchMap$(() => this.cloneHlp(hlpId, true)));
              const noAnswer$ = answer$.pipe(filter$(noAnswer), map$(() => throwError$('msg_pb_withdrawn_denied_hlp')), switchAll$());
              return concat$(yesAnswer$, noAnswer$);
            } else {
              return throwError$(errors);
            }
          }),
        ).subscribe(
          (newHlp) => {
            setLocalValue('clonedHlp', newHlp);
            this.router.navigateToChild(['/sales/hlp/new'], {
                parentTitle: 'lbl_hlps',
                queryParams: {
                  clonedHlp: hlp.id,
                },
              });
          },
          this.notifications.processAll);
        return;
      }
  }

  onCreateQuote(hlp: HlpListItem): void {
    this.dialogService.openWithInput(hlpCreateLeadQuoteDialogRoute, <HlpCreateLeadQuoteDialogConfig>{
                                       hlp,
                                     },
                                     {
                                       autoFocus: false,
                                     },
    ).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {});
  }

  private cloneHlp(hlpId: ID, skipWithdrawnWarningCheck?: boolean): Observable<any> {
    return this.hlpRestService.getClone(hlpId, skipWithdrawnWarningCheck || false);
  }

  private deleteHlp(hlp: HlpListItem, listingStatus?: ListingStatus): void {
    this.businessService
      .delete(hlp.id, listingStatus).pipe(
      this.waitingService.operator()).subscribe(
      () => {
        this.dataSource = this.businessService.findAllAsDataSource(this.filter);
      },
      (error: MessageBasket) => this.notifications.processAll(error),
    );
  }
}
